import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useConversations, {
  type BinaryFeedbackFilter,
  type ConversationVisibility,
  type SortKey,
  type SortOrder,
} from "admin/hooks/useConversations";
import {
  getReviewStatusDisplay,
  getReviewStatusColor,
  REVIEW_STATUS_OPTIONS,
} from "admin/types/constants/review-status";
import type Conversation from "admin/types/interfaces/conversation";
import type { ReviewStatus } from "admin/types/interfaces/conversation";
import { formatDistanceToNowStrict } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Copy,
  Loader2,
  type LucideIcon,
  MessagesSquare,
  MessageSquareText,
  Pencil,
  Search,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import { ArrayParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { MultiSelect } from "~/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useUser from "~/contexts/user";
import useUserPermission from "~/hooks/useUserPermission";
import { cn } from "~/lib/utils";
import { useCanManageAIAgents } from "~/pages/manifold-nextgen/hooks/useCanManageAIAgents";
import { nextgenAdminConversationPath } from "~/pages/manifold-nextgen/paths";
import { VIEW_ALL_AI_CONVERSATIONS } from "~/router/permissions";
import { getOwnerDisplayName } from "~/utils/strings";

const SMALLEST_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const MinLengthMessage = ({ show }: { show: boolean }) => {
  return show ? (
    <p
      className="absolute top-full left-0 text-xs text-destructive mt-1 whitespace-nowrap"
      aria-live="polite"
    >
      Min {MIN_SEARCH_LENGTH} characters
    </p>
  ) : null;
};

const CountCell = ({ value, className }: { value: number; className?: string }) => (
  <TableCell className={className}>
    <span className={cn("text-[13px]", value > 0 ? "text-foreground" : "text-muted-foreground/50")}>
      {value}
    </span>
  </TableCell>
);

// Inline positive/negative/comment counts; each dims to 40% when zero.
const FeedbackCell = ({
  positive,
  negative,
  comments,
  className,
}: {
  positive: number;
  negative: number;
  comments: number;
  className?: string;
}) => {
  const items: { Icon: LucideIcon; value: number; label: string }[] = [
    { Icon: ThumbsUp, value: positive, label: "Positive feedback" },
    { Icon: ThumbsDown, value: negative, label: "Negative feedback" },
    { Icon: MessageSquareText, value: comments, label: "Comments" },
  ];
  return (
    <TableCell className={className}>
      <div className="flex items-center gap-2.5">
        {items.map(({ Icon, value, label }) => (
          <span
            key={label}
            aria-label={`${label}: ${value}`}
            className={cn(
              "inline-flex items-center gap-1 text-[13px]",
              value > 0 ? "text-foreground" : "text-muted-foreground/40"
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {value}
          </span>
        ))}
      </div>
    </TableCell>
  );
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${month}/${day}/${year} ${hours}:${minutes}${ampm}`;
};

const getEmailInitial = (email: string) => {
  return email.charAt(0).toUpperCase();
};

const getConversationOwnerDisplayName = (conversation: Conversation) =>
  getOwnerDisplayName(
    conversation.ownerGivenName,
    conversation.ownerFamilyName,
    conversation.userId
  );

// Prefer the human-friendly project short_id; fall back to the uuid until the
// short_id backfill migration completes (it is nullable for older projects).
const getProjectDisplayId = (conversation: Conversation) =>
  conversation.projectShortId ?? conversation.projectId;

// Prefer the full project name; fall back to the short_id/uuid for older
// conversations whose project is unnamed or not linked.
const getProjectDisplayName = (conversation: Conversation) =>
  conversation.projectName ?? getProjectDisplayId(conversation);

const FEEDBACK_OPTIONS: { value: BinaryFeedbackFilter; label: string }[] = [
  { value: "POSITIVE", label: "Has positive" },
  { value: "NEGATIVE", label: "Has negative" },
];

const SORTABLE_COLUMN_LABELS: Record<SortKey, string> = {
  updated_at: "Updated",
  created_at: "Created",
  number_of_annotations: "Annotations",
  number_of_turns: "Turns",
  number_of_positive_feedback: "Positive feedback",
  number_of_negative_feedback: "Negative feedback",
  number_of_comments: "Comments",
};

// Subtitle beneath the Sessions header, describing which conversations the
// current result set includes so users aren't confused about why a conversation
// is or isn't shown. The scope depends on the caller's permissions.
const VISIBILITY_NOTES: Record<ConversationVisibility, string> = {
  OWN_ONLY: "Your conversations with this agent.",
  FEEDBACK_AND_OWN: "Your conversations with this agent, plus any others with feedback.",
  ALL: "All conversations users have had with this agent.",
};

// Hoisted to module scope so `withDefault`'s default arrays and decode functions keep stable
// references across renders. Inline config caused `query.feedback`/`reviewStatus` to
// return new array refs every render, which retriggered the search effect infinitely.
const QUERY_PARAMS_CONFIG = {
  user: withDefault(StringParam, ""),
  id: withDefault(StringParam, ""),
  feedback: withDefault(ArrayParam, [] as string[]),
  reviewStatus: withDefault(ArrayParam, [] as string[]),
  comments: withDefault(StringParam, "all"),
  annotations: withDefault(StringParam, "all"),
  sortKey: withDefault(StringParam, "updated_at"),
  sortOrder: withDefault(StringParam, "desc"),
};

interface AgentConversationTableProps {
  agentId: string;
}

const AgentConversationTable = ({ agentId }: AgentConversationTableProps) => {
  const navigate = useNavigate();
  // Admins see cross-user data and admin-only columns; everyone else sees only
  // their own conversations. manage:agents counts as admin for this per-agent view.
  const canViewAllConversations = useUserPermission(VIEW_ALL_AI_CONVERSATIONS);
  const canManageAgents = useCanManageAIAgents();
  const isAdmin = canViewAllConversations || canManageAgents;
  // Permissions resolve once /me loads (user is null until then). Defer the
  // fetch until then so we hit the correct endpoint rather than flashing the
  // self-scoped view to admins.
  const permissionsLoaded = useUser() !== null;
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(SMALLEST_PAGE_SIZE);

  const [query, setQuery] = useQueryParams(QUERY_PARAMS_CONFIG);

  const [localUserSearch, setLocalUserSearch] = useState(query.user);
  const [localIdSearch, setLocalIdSearch] = useState(query.id);

  const userSearch = query.user;
  const idSearch = query.id;
  const feedbackFilter = useMemo(
    () => (query.feedback ?? []).filter(Boolean) as BinaryFeedbackFilter[],
    [query.feedback]
  );
  // Annotation/review-status filters are admin-only and ignored by the self-scoped
  // endpoint, so treat them as unset for non-admins (keeps the request and the
  // active-filter indicator honest).
  const reviewStatusFilter = useMemo(
    () => (isAdmin ? ((query.reviewStatus ?? []).filter(Boolean) as ReviewStatus[]) : []),
    [query.reviewStatus, isAdmin]
  );
  const hasComments = query.comments === "all" ? null : query.comments === "yes";
  const hasAnnotations =
    isAdmin && query.annotations !== "all" ? query.annotations === "yes" : null;
  const sortKey = query.sortKey as SortKey;
  const sortOrder = query.sortOrder as SortOrder;

  const debouncedUserSearchInput = useDebounce(localUserSearch, SEARCH_DEBOUNCE_MS);
  const debouncedIdSearchInput = useDebounce(localIdSearch, SEARCH_DEBOUNCE_MS);

  const debouncedUserSearch = useDebounce(userSearch, SEARCH_DEBOUNCE_MS);
  const debouncedIdSearch = useDebounce(idSearch, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedUserSearchInput !== userSearch) {
      setQuery({ user: debouncedUserSearchInput });
    }
  }, [debouncedUserSearchInput, userSearch, setQuery]);

  useEffect(() => {
    if (debouncedIdSearchInput !== idSearch) {
      setQuery({ id: debouncedIdSearchInput });
    }
  }, [debouncedIdSearchInput, idSearch, setQuery]);

  useEffect(() => {
    setLocalUserSearch(userSearch);
  }, [userSearch]);

  useEffect(() => {
    setLocalIdSearch(idSearch);
  }, [idSearch]);

  const offset = (pageNum - 1) * pageSize;

  const {
    data: conversations = [],
    total,
    visibility,
    isLoading,
    error,
  } = useConversations({
    limit: pageSize,
    offset,
    userId: debouncedUserSearch.length >= MIN_SEARCH_LENGTH ? debouncedUserSearch : undefined,
    agentId,
    projectId: debouncedIdSearch.length >= MIN_SEARCH_LENGTH ? debouncedIdSearch : undefined,
    feedbackFilter,
    hasComments,
    hasAnnotations,
    reviewStatus: reviewStatusFilter,
    sortKey,
    sortOrder,
    isAdmin,
    enabled: permissionsLoaded,
  });

  useEffect(() => {
    setPageNum(1);
  }, [
    debouncedUserSearch,
    agentId,
    debouncedIdSearch,
    feedbackFilter,
    hasComments,
    hasAnnotations,
    reviewStatusFilter,
    sortKey,
    sortOrder,
  ]);

  const hasActiveFilters = useMemo(() => {
    return (
      idSearch !== "" ||
      userSearch !== "" ||
      feedbackFilter.length > 0 ||
      hasComments !== null ||
      hasAnnotations !== null ||
      reviewStatusFilter.length > 0 ||
      sortKey !== "updated_at" ||
      sortOrder !== "desc"
    );
  }, [
    idSearch,
    userSearch,
    feedbackFilter,
    hasComments,
    hasAnnotations,
    reviewStatusFilter,
    sortKey,
    sortOrder,
  ]);

  const handleResetFilters = () => {
    setLocalIdSearch("");
    setLocalUserSearch("");
    setQuery({
      id: "",
      user: "",
      feedback: [],
      reviewStatus: [],
      comments: "all",
      annotations: "all",
      sortKey: "updated_at",
      sortOrder: "desc",
    });
  };

  const handleSortClick = (key: SortKey) => {
    if (sortKey === key) {
      setQuery({ sortOrder: sortOrder === "desc" ? "asc" : "desc" });
    } else {
      setQuery({ sortKey: key, sortOrder: "desc" });
    }
  };

  const pageCount = Math.ceil((total || 0) / pageSize);
  const start = (pageNum - 1) * pageSize + 1;
  const end = Math.min(pageNum * pageSize, total || 0);

  const handlePageChange = (newPage: number) => {
    setPageNum(newPage);
    scrollableRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNum(1);
  };

  const handleViewConversation = (conversationId: string) => {
    navigate(nextgenAdminConversationPath(conversationId));
  };

  const handleRowMouseDown = () => {
    setIsMouseDown(true);
    setIsDragging(false);
  };

  const handleRowMouseMove = () => {
    if (isMouseDown) {
      setIsDragging(true);
    }
  };

  const handleRowMouseUp = () => {
    setIsMouseDown(false);
  };

  const handleRowClick = (conversationId: string) => {
    if (isDragging) {
      return;
    }
    handleViewConversation(conversationId);
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortKey !== key) {
      return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />;
    }
    return sortOrder === "asc" ? (
      <ArrowUp className="ml-1 h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="ml-1 h-3.5 w-3.5" />
    );
  };

  const renderSortableHeader = (columnKey: SortKey) => (
    <button
      type="button"
      onClick={() => handleSortClick(columnKey)}
      className="inline-flex items-center text-left font-medium hover:text-foreground transition-colors cursor-pointer"
    >
      {SORTABLE_COLUMN_LABELS[columnKey]}
      {renderSortIcon(columnKey)}
    </button>
  );

  const renderSortableIconHeader = (columnKey: SortKey, Icon: LucideIcon, label: string) => (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => handleSortClick(columnKey)}
          aria-label={`Sort by ${label}`}
          className="inline-flex items-center hover:text-foreground transition-colors cursor-pointer"
        >
          <Icon className="h-4 w-4 text-muted-foreground" />
          {renderSortIcon(columnKey)}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );

  // User, Status, and Annotations are admin-only; everyone sees the other 7 columns.
  const columnCount = isAdmin ? 10 : 7;

  return (
    <div className="flex flex-col h-full min-h-0 pt-2">
      {visibility && (
        <p className="text-sm text-muted-foreground mb-4" data-testid="visibility-note">
          {VISIBILITY_NOTES[visibility]}
        </p>
      )}
      {/* Filter Bar */}
      <div data-testid="conversation-filter-bar" className="flex flex-wrap items-center gap-3 mb-4">
        {isAdmin && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="User..."
              value={localUserSearch}
              onChange={(e) => setLocalUserSearch(e.target.value)}
              className="pl-10 pr-8 w-[200px] h-9"
              name="user filter field"
            />
            {localUserSearch && (
              <button
                type="button"
                onClick={() => setLocalUserSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                aria-label="Clear user search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <MinLengthMessage
              show={
                debouncedUserSearch.length > 0 && debouncedUserSearch.length < MIN_SEARCH_LENGTH
              }
            />
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Project..."
            value={localIdSearch}
            onChange={(e) => setLocalIdSearch(e.target.value)}
            className="pl-10 pr-8 w-[200px] h-9"
            name="id filter field"
          />
          {localIdSearch && (
            <button
              type="button"
              onClick={() => setLocalIdSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              aria-label="Clear ID search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <MinLengthMessage
            show={debouncedIdSearch.length > 0 && debouncedIdSearch.length < MIN_SEARCH_LENGTH}
          />
        </div>

        <MultiSelect
          options={FEEDBACK_OPTIONS}
          value={feedbackFilter}
          onValueChange={(values) =>
            setQuery({ feedback: values.length === 0 ? [] : (values as string[]) })
          }
          placeholder="Feedback"
          className="w-[180px]"
        />

        <Select value={query.comments} onValueChange={(value) => setQuery({ comments: value })}>
          <SelectTrigger aria-label="Comments" className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All comments</SelectItem>
            <SelectItem value="yes">With comments</SelectItem>
            <SelectItem value="no">Without comments</SelectItem>
          </SelectContent>
        </Select>

        {isAdmin && (
          <MultiSelect
            options={REVIEW_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
            value={reviewStatusFilter}
            onValueChange={(values) =>
              setQuery({ reviewStatus: values.length === 0 ? [] : (values as string[]) })
            }
            placeholder="Review Status"
            className="w-[200px]"
          />
        )}

        {isAdmin && (
          <Select
            value={query.annotations}
            onValueChange={(value) => setQuery({ annotations: value })}
          >
            <SelectTrigger aria-label="Annotations" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All annotations</SelectItem>
              <SelectItem value="yes">With annotations</SelectItem>
              <SelectItem value="no">Without annotations</SelectItem>
            </SelectContent>
          </Select>
        )}

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetFilters}
            className="h-9 px-3 text-xs"
          >
            Reset
          </Button>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0 pb-6">
        <div className="flex-1 flex flex-col rounded-lg border min-h-0">
          <div ref={scrollableRef} className="flex-1 overflow-auto min-h-0">
            <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[240px]">Name</TableHead>
                  <TableHead className="w-[200px]">Project</TableHead>
                  {isAdmin && <TableHead className="w-[180px]">User</TableHead>}
                  <TableHead className="w-[70px]">
                    {renderSortableIconHeader("number_of_turns", MessagesSquare, "Turns")}
                  </TableHead>
                  <TableHead className="w-[150px]">Feedback</TableHead>
                  <TableHead className="w-[100px]">Impact</TableHead>
                  <TableHead className="w-[110px]">{renderSortableHeader("created_at")}</TableHead>
                  <TableHead className="w-[110px]">{renderSortableHeader("updated_at")}</TableHead>
                  {isAdmin && <TableHead className="w-[100px]">Status</TableHead>}
                  {isAdmin && (
                    <TableHead className="w-[90px]">
                      {renderSortableIconHeader("number_of_annotations", Pencil, "Annotations")}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {error ? (
                  <TableRow className="pointer-events-none">
                    <TableCell colSpan={columnCount} className="text-center text-destructive py-8">
                      Error loading conversations
                    </TableCell>
                  </TableRow>
                ) : isLoading || !permissionsLoaded ? (
                  <TableRow className="pointer-events-none">
                    <TableCell
                      colSpan={columnCount}
                      className="text-center text-muted-foreground py-8"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading conversations...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : conversations.length === 0 ? (
                  <TableRow className="pointer-events-none">
                    <TableCell
                      colSpan={columnCount}
                      className="text-center text-muted-foreground py-8"
                    >
                      No conversations found
                    </TableCell>
                  </TableRow>
                ) : (
                  conversations.map((conversation) => (
                    <TableRow
                      key={conversation.id}
                      onMouseDown={handleRowMouseDown}
                      onMouseMove={handleRowMouseMove}
                      onMouseUp={handleRowMouseUp}
                      onClick={() => handleRowClick(conversation.id)}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <TableCell className="w-[240px]">
                        <div className="flex flex-col min-w-0 gap-0.5">
                          <span className="font-semibold truncate">
                            {conversation.displayName || "Untitled"}
                          </span>
                          {conversation.datasetName && (
                            <span className="text-xs text-muted-foreground truncate">
                              Dataset: {conversation.datasetName}
                            </span>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="w-[200px]">
                        <div className="flex items-center gap-1 min-w-0 group">
                          <span className="text-[13px] truncate">
                            {getProjectDisplayName(conversation)}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const projectValue = getProjectDisplayId(conversation);
                              navigator.clipboard.writeText(projectValue);
                              setCopiedId(projectValue);
                              setTimeout(() => setCopiedId(null), 2000);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 cursor-pointer"
                            aria-label="Copy Project"
                          >
                            {copiedId === getProjectDisplayId(conversation) ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                            )}
                          </button>
                        </div>
                      </TableCell>

                      {isAdmin && (
                        <TableCell className="w-[180px]">
                          <Tooltip delayDuration={500}>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2 min-w-0">
                                <Avatar className="h-5 w-5 shrink-0">
                                  <AvatarImage src={conversation.ownerImageUrl ?? undefined} />
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                                    {getEmailInitial(conversation.userId)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-[13px] text-muted-foreground truncate">
                                  {getConversationOwnerDisplayName(conversation)}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>{conversation.userId}</TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}

                      <CountCell className="w-[70px]" value={conversation.turnCount ?? 0} />

                      <FeedbackCell
                        className="w-[150px]"
                        positive={conversation.positiveFeedbackCount ?? 0}
                        negative={conversation.negativeFeedbackCount ?? 0}
                        comments={conversation.commentsCount ?? 0}
                      />

                      <TableCell className="w-[100px]">
                        <div className="flex flex-col gap-1">
                          <ImpactBar
                            level={conversation.maxPositiveImpact ?? null}
                            variant="positive"
                          />
                          <ImpactBar
                            level={conversation.maxNegativeImpact ?? null}
                            variant="negative"
                          />
                        </div>
                      </TableCell>

                      <TableCell className="w-[110px]">
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <span className="text-[13px] text-muted-foreground">
                              {formatDistanceToNowStrict(new Date(conversation.createdAt), {
                                addSuffix: true,
                              })}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{formatDate(conversation.createdAt)}</TooltipContent>
                        </Tooltip>
                      </TableCell>

                      <TableCell className="w-[110px]">
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <span className="text-[13px] text-muted-foreground">
                              {formatDistanceToNowStrict(new Date(conversation.updatedAt), {
                                addSuffix: true,
                              })}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{formatDate(conversation.updatedAt)}</TooltipContent>
                        </Tooltip>
                      </TableCell>

                      {isAdmin && (
                        <TableCell className="w-[100px]">
                          <span
                            className={cn(
                              "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                              getReviewStatusColor(conversation.reviewStatus)
                            )}
                          >
                            {getReviewStatusDisplay(conversation.reviewStatus)}
                          </span>
                        </TableCell>
                      )}

                      {isAdmin && (
                        <CountCell
                          className="w-[90px]"
                          value={conversation.annotationsCount ?? 0}
                        />
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        {total !== undefined && total > Math.min(pageSize, SMALLEST_PAGE_SIZE) && (
          <div className="flex items-center justify-between py-4 flex-shrink-0">
            <div className="text-sm text-muted-foreground">
              {`${start} - ${end} of ${total.toLocaleString()} results`}
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Per page:</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => handlePageSizeChange(Number(value))}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={`${SMALLEST_PAGE_SIZE}`}>{SMALLEST_PAGE_SIZE}</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(1)}
                  disabled={pageNum === 1}
                >
                  <ChevronsLeft />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(pageNum - 1)}
                  disabled={pageNum === 1}
                >
                  <ChevronLeft />
                </Button>
                <span className="px-4 text-sm text-muted-foreground">
                  Page {pageNum} of {pageCount}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(pageNum + 1)}
                  disabled={pageNum === pageCount}
                >
                  <ChevronRight />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(pageCount)}
                  disabled={pageNum === pageCount}
                >
                  <ChevronsRight />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function ImpactBar({ level, variant }: { level: number | null; variant: "positive" | "negative" }) {
  if (level == null) return null;
  const filled = variant === "positive" ? "bg-green-500" : "bg-red-500";
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={cn(
            "h-2 w-3",
            i === 1 && "rounded-l-full",
            i === 4 && "rounded-r-full",
            i > 1 && i < 4 && "rounded-none",
            i <= level ? filled : "bg-gray-200"
          )}
        />
      ))}
    </div>
  );
}

export default AgentConversationTable;
